// Lib
import { captureException } from '@sentry/browser';
import { put, select, takeEvery } from 'redux-saga/effects';
import type { Action } from 'redux-actions';

// Api
import client from '@api';

// Actions
import { ErrorFeed, GetAccountFeed, SetFeed } from '@actions/feed';
import { SetLoading } from '@actions/loading';

// Graph
import { authFeedQuery } from '../graphql/queries/feed';

// Selectors
import { getPostsListByReducerSelector } from '@selectors';

// Types
interface Post {
  _id: string;
  [key: string]: unknown;
}

interface SetFeedSagaParams {
  cacheKey?: string | null;
  posts: Post[];
  network: string;
  skip: number;
}

interface FeedState {
  posts: Post[];
}

interface AccountFeedPayload {
  accountId: string;
  source?: string;
  authId?: string;
  postType?: string;
  skip: number;
  [key: string]: unknown;
}

interface AuthFeedResponse {
  data: {
    authFeed: {
      items: Post[];
    };
  };
}

export function* setFeedSaga({
  cacheKey = null,
  posts,
  network,
  skip,
}: SetFeedSagaParams): Generator<unknown, void, unknown> {
  try {
    const { posts: CachedPosts } = (yield select(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (state: {
        Posts: Record<string, Post>;
        Feed: Record<string, FeedState>;
      }) => getPostsListByReducerSelector(state as any, network),
    )) as FeedState;
    yield put(
      SetFeed({
        network,
        cacheKey,
        hasNext: !!posts.length, // && !!cacheKey,
        posts:
          skip !== 0
            ? [...CachedPosts, ...posts].uniqBy('_id')
            : posts.uniqBy('_id'),
      }),
    );
  } catch (error) {
    yield put(ErrorFeed(error));
    captureException(error);
  }
}

function* getAccountFeedSaga({
  payload: { accountId, source, ...props },
}: Action<AccountFeedPayload>): Generator<unknown, void, unknown> {
  try {
    yield put(SetLoading(true));
    const response = (yield client().query({
      query: authFeedQuery,
      variables: { input: props },
      fetchPolicy: 'no-cache',
    })) as AuthFeedResponse;

    const posts = response.data.authFeed;

    const network = source
      ? `${accountId}_${
          props?.postType ? source + '_' + props?.postType : source
        }_${props.authId}`
      : accountId;

    yield setFeedSaga({ posts: posts?.items, network, skip: props.skip });
    yield put(SetLoading(false));
  } catch (error) {
    yield put(ErrorFeed(error));
    captureException(error);
    yield put(SetLoading(false));
  }
}

function* feedSaga(): Generator<unknown, void, unknown> {
  yield takeEvery(GetAccountFeed, getAccountFeedSaga);
}

export default feedSaga;
